import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import { useState, useEffect } from "react";
import PageShell from "../../components/PageShell";
import OutPatientsTable from "./PatientsTable.jsx";
import InPatientsTable from "./InPatientsTable.jsx";
import EmergencyTable from "./EmergencyTable.jsx";
import OpdReports from "../inPatientDetail/OpdReports";
import IpdReports from "../inPatientDetail/IpdReports";
// AppointmentConfirmations is now a separate page, not a tab
// import AppointmentConfirmations from "./AppointmentConfirmations";
import { useLocation } from "react-router-dom";
import { useSubscriptionCapabilities } from "../../hooks/useSubscriptionCapabilities";
import {
  PAGE_TABS_BAR_SX,
  PAGE_TAB_LIST_PROPS,
  TAB_PANEL_SX,
} from "../../utils/pageLayoutStyles";

const ManagePatient = ({ defaultTab }) => { 
  const location = useLocation();
  const { hasCapability, isPremiumPackage, loading: capabilitiesLoading } = useSubscriptionCapabilities();
  
  // Check capabilities for tab visibility
  const hasOpdCapability = hasCapability("OPD");
  const hasIpdCapability = hasCapability("IPD") || hasCapability("In Patient");
  // IN patient / admission UI: Premium only (Basic & Intermediate are OUT-only)
  const canUseInPatient = isPremiumPackage && hasIpdCapability;
  // Initialize state based on navigation state or defaultTab prop
  const getInitialState = () => {
    const { showIpdTimeline: navShowIpdTimeline, selectedPatientId: navPatientId, selectedTab: navTab } = location?.state || {};
    
    if (navShowIpdTimeline && navPatientId) {
      console.log("Initializing with IPD timeline for patient:", navPatientId);
      return {
        selectedTab: navTab || "2",
        selectedPatientId: navPatientId,
        showOpdTimeline: false,
        showIpdTimeline: true
      };
    }
    
    // Use defaultTab prop if provided, otherwise default based on available capabilities
    // Default to Out Patient (3) if OPD is available, otherwise In Patient (2) if IPD is available (Premium)
    let defaultTabValue = defaultTab;
    if (!defaultTabValue) {
      if (hasOpdCapability) {
        defaultTabValue = "3"; // Out Patient
      } else if (canUseInPatient) {
        defaultTabValue = "2"; // In Patient
      } else {
        defaultTabValue = "3"; // Fallback to Out Patient
      }
    }
    
    return {
      selectedTab: defaultTabValue,
      selectedPatientId: null,
      showOpdTimeline: false,
      showIpdTimeline: false
    };
  };

  const initialState = getInitialState();
  const [selectedTab, setSelectedTab] = useState(initialState.selectedTab);
  
  // Update selected tab when capabilities are loaded or change to ensure a valid tab is selected
  useEffect(() => {
    // Skip if capabilities are still loading
    if (capabilitiesLoading) {
      return;
    }
    
    // If current tab is not available due to capability/package restrictions, switch to a valid tab
    if (!canUseInPatient && (selectedTab === "2" || selectedTab === "4")) {
      // If OPD is available, switch to Out Patient tab
      if (hasOpdCapability) {
        setSelectedTab("3"); // Switch to Out Patient tab
      }
    } else if (!hasOpdCapability && selectedTab === "3") {
      // If OPD is not available but IPD is (Premium), switch to In Patient tab
      if (canUseInPatient) {
        setSelectedTab("2"); // Switch to In Patient tab
      }
    }
  }, [canUseInPatient, hasOpdCapability, capabilitiesLoading, selectedTab]);
  const [selectedPatientId, setSelectedPatientId] = useState(initialState.selectedPatientId);
  const [showOpdTimeline, setShowOpdTimeline] = useState(initialState.showOpdTimeline);
  const [showIpdTimeline, setShowIpdTimeline] = useState(initialState.showIpdTimeline);
  
  const handleChange = (event, newValue) => {
    setSelectedTab(newValue);
    // Reset timelines when switching tabs
    setShowOpdTimeline(false);
    setShowIpdTimeline(false);
    setSelectedPatientId(null);
  };


  // Handle OPD patient timeline display
  const handleOpdPatientClick = (patientId) => {
    setSelectedPatientId(patientId);
    setShowOpdTimeline(true);
  };

  // Handle IPD patient timeline display
  const handleIpdPatientClick = (patientId) => {
    setSelectedPatientId(patientId);
    setShowIpdTimeline(true);
  };

  console.log("ManagePatient render - selectedTab:", selectedTab, "showIpdTimeline:", showIpdTimeline, "selectedPatientId:", selectedPatientId);

  return (
    <PageShell title="PATIENTS">
      <TabContext value={selectedTab}>
        <Box sx={PAGE_TABS_BAR_SX}>
          <TabList
            {...PAGE_TAB_LIST_PROPS}
            onChange={handleChange}
          >
            {canUseInPatient && <Tab label="In Patients" value="2" />}
            {hasOpdCapability && <Tab label="Out Patients" value="3" />}
            {canUseInPatient && <Tab label="Emergency Admission" value="4" />}
            {/* Appointment Confirmations tab removed - now a separate page */}
          </TabList>
        </Box>
        {canUseInPatient && (
          <TabPanel value="2" sx={TAB_PANEL_SX}>
            {showIpdTimeline && selectedPatientId ? (
              <IpdReports 
                patientId={selectedPatientId} 
                onBack={() => {
                  setShowIpdTimeline(false);
                  setSelectedPatientId(null);
                }}
              />
            ) : (
              <InPatientsTable onPatientClick={handleIpdPatientClick} />
            )}
          </TabPanel>
        )}
        {hasOpdCapability && (
          <TabPanel value="3" sx={TAB_PANEL_SX}>
            {showOpdTimeline && selectedPatientId ? (
              <OpdReports 
                patientId={selectedPatientId} 
                onBack={() => {
                  setShowOpdTimeline(false);
                  setSelectedPatientId(null);
                }}
              />
            ) : (
              <OutPatientsTable onPatientClick={handleOpdPatientClick} />
            )}
          </TabPanel>
        )}
        {canUseInPatient && (
          <TabPanel value="4" sx={TAB_PANEL_SX}>
            <EmergencyTable />
          </TabPanel>
        )}
        {/* Appointment Confirmations tab removed - now a separate page at /appointment-confirmations */}
      </TabContext>
    </PageShell>
  );
};

export default ManagePatient;
